import { useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { yandexLogin, YANDEX_OAUTH } from "../services/authService";
import { linkYandexAccount } from "../services/usersService";
import { claimOauthCode } from "../utils/oauthCodeGuard";
import { classifyOauthReturn, linkErrorMessage, readVkReturn, readYandexReturn, VERIFY_PAGE_PATH } from "../utils/verifyAccount";
import { needsOauthRegistration } from "./oauthRegistration";
import { PATHS } from "./paths";
import { RouteLoader } from "./RouteLoader";

/**
 * YandexOAuthGate — прозрачный переход при входе через Яндекс.
 *
 * Проблема: ProtectedRoute стоит перед /app, проверяет isAuthenticated,
 * а он при первом заходе с ?code=… ложный — редиректит на лендинг, теряя код.
 *
 * Решение: этот компонент НАД ProtectedRoute. Он видит ?code=, обменивает его
 * на токены, обновляет AuthContext, и только потом отдаёт дальше ProtectedRoute.
 * После успешного обмена делает window.location.replace("/app") — на втором
 * круге сессия уже жива, и ProtectedRoute пропускает. При отказе обмена
 * показывает toast.error с текстом ошибки бэкенда и только потом отдаёт
 * управление ProtectedRoute (который уведёт на лендинг).
 */
export function YandexOAuthGate({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, loading, refreshUser } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [exchanging, setExchanging] = useState(false);
    const attemptedRef = useRef(false);
    const linkAttemptedRef = useRef(false);

    // #1296: возврат из Яндекса после «Привязать Yandex ID» в профиле. Сессия жива -- код уходит в
    // привязку к ТЕКУЩЕМУ аккаунту, а не во вход; принимается только при совпавшем `state`.
    useEffect(() => {
        if (loading || !isAuthenticated || linkAttemptedRef.current) return;
        // Возврат VK ID после «Привязать VK ID»: целиком перезагруженную страницу возвращаем туда, откуда начали,
        // а код отдаём странице верификации (она завершит привязку через SDK). Без записи привязки не трогаем.
        if (classifyOauthReturn(location.search, true) === "vk") {
            const vk = readVkReturn(window.sessionStorage, location.search);
            if (vk) {
                linkAttemptedRef.current = true;
                navigate(vk.returnPath, { replace: true, state: { vkReturn: vk.vkReturn } });
            }
            return;
        }
        // #1303: возврат VK ID (`device_id`) не Яндекса -- не разбираем и не гасим запись привязки.
        if (classifyOauthReturn(location.search, true) !== "yandex-link") return;
        const ret = readYandexReturn(window.sessionStorage, location.search);
        if (ret.kind === "none") return;
        linkAttemptedRef.current = true;
        if (ret.kind === "stale") {
            toast.error("Сессия истекла, войдите и повторите привязку Yandex ID.");
            navigate(VERIFY_PAGE_PATH, { replace: true });
            return;
        }
        if (ret.kind === "cancelled") {
            toast.error("Привязка Yandex ID отменена.");
            navigate(ret.returnPath || VERIFY_PAGE_PATH, { replace: true });
            return;
        }
        setExchanging(true);
        void (async () => {
            const res = await linkYandexAccount({ code: ret.code, redirectUri: YANDEX_OAUTH.redirectUri });
            if (res.data) {
                toast.success("Yandex ID привязан");
                await refreshUser();
            } else {
                toast.error(linkErrorMessage(res.code, res.error || "Не удалось привязать Yandex ID"));
            }
            setExchanging(false);
            navigate(ret.returnPath || VERIFY_PAGE_PATH, { replace: true });
        })();
    }, [loading, isAuthenticated, location.search, refreshUser, navigate]);

    useEffect(() => {
        // Только когда AuthContext загрузился, мы ещё не аутентифицированы,
        // а в URL есть ?code=… — и только первый раз.
        if (loading || isAuthenticated || attemptedRef.current) return;
        const owner = classifyOauthReturn(location.search, false);
        if (owner === "none" || owner === "vk") return; // VK-код в Яндекс не отдаём (#1303)
        const params = new URLSearchParams(location.search);
        const code = params.get("code");
        // `state` посылает только привязка (#1296): без сессии такой код входом не становится.
        if (params.get("state")) {
            attemptedRef.current = true;
            readYandexReturn(window.sessionStorage, location.search); // гасит запись привязки
            toast.error("Сессия истекла, войдите и повторите привязку Yandex ID.");
            navigate(location.pathname, { replace: true });
            return;
        }
        if (!code) return;
        attemptedRef.current = true;
        // #1307: `code` убираем из адресной строки до обмена -- обновление/«назад» не пошлёт его снова.
        try {
            const clean = new URLSearchParams(location.search);
            clean.delete("code");
            const qs = clean.toString();
            window.history.replaceState(window.history.state, "", location.pathname + (qs ? `?${qs}` : "") + location.hash);
        } catch {
            /* не критично: защита от повтора ниже */
        }
        // Уже обменянный код на бэкенд не шлём (иначе invalid_grant): сразу понятное сообщение.
        let storage: Storage | null = null;
        try {
            storage = window.sessionStorage;
        } catch {
            storage = null;
        }
        if (!claimOauthCode(storage, code)) {
            toast.error("Код входа устарел, войдите через Яндекс ещё раз.");
            return;
        }
        setExchanging(true);

        void (async () => {
            const res = await yandexLogin({ code, redirectUri: YANDEX_OAUTH.redirectUri });
            if (res.data) {
                // Обновить контекст, чтобы ProtectedRoute увидел сессию,
                // и перезагрузить страницу без ?code= в адресе.
                await refreshUser();
                // #1279: новый OAuth-аккаунт без анкеты — сразу в визард регистрации,
                // минуя ленту (иначе туда же уведёт OAuthRegistrationGate, но лишним кругом).
                window.location.replace(
                    needsOauthRegistration(res.data.user) ? PATHS.registerComplete : PATHS.app,
                );
                // Дальше код не выполняется — страница перезагружается.
                return;
            }
            // Ошибка — ProtectedRoute уведёт на лендинг. Без тоста это выглядело
            // как «нажал Войти через Яндекс — и ничего»: причина (например,
            // 503 «Yandex OAuth не настроен» с прода) не показывалась нигде.
            toast.error(res.error || "Не удалось войти через Яндекс");
            setExchanging(false);
        })();
    }, [loading, isAuthenticated, location.search, location.pathname, refreshUser, navigate]);

    if (loading || exchanging) return <RouteLoader />;

    return <>{children}</>;
}

/** Layout-гард авторизации: неавторизованных уводит на лендинг, пока грузится сессия — спиннер. */
export function ProtectedRoute() {
    const { isAuthenticated, loading } = useAuth();

    if (loading) return <RouteLoader/>;

    if (!isAuthenticated) {
        return <Navigate to={PATHS.landing} replace/>;
    }

    return <Outlet/>;
}